// Q9 S4 — Pure conditional-reveal rules for the VisitReport wizard.
// See docs/specs/visit-report-conditional-rules.md for the binding rule
// definitions + the test-case matrix this implements.
//
// Locks honored:
//   D1  — Wellbeing co-fires with Clinical (same trigger).
//   D2  — Medication: hasActiveMeds === true (live count, cached at mount).
//   D3  — Mobility: baseline_mobility !== "independent" (or override).
//   D4  — Clinical/Wellbeing: visit_type !== "standard_wellness".
//   D5  — Client-side rule evaluation. No new endpoint.
//   D6  — Frozen at wizard mount. The single legitimate exception is
//         step-0 visit_type fallback: caller recomputes once after the
//         step-0 PATCH succeeds. Caller responsibility — this function
//         is pure.
//   D24 — 'review' is the terminal step.

import type { MobilityStatus, VisitType } from "@hcm/core";

export const SECTION_STEPS = [
  "vitals", "mobility", "clinical", "wellbeing", "medication", "notes",
] as const;
export type SectionStep = (typeof SECTION_STEPS)[number];

// Wizard-level steps include the step-0 picker and the terminal review.
// Section steps map 1:1 to backend section endpoints; "visit-type" + "review"
// are wizard-only and never hit the per-section PATCH endpoints.
export type WizardStep = SectionStep | "visit-type" | "review";

export interface ConditionalContext {
  /** Patient's documented baseline mobility (D3 trigger source). */
  baselineMobility: MobilityStatus | null | undefined;
  /** SR.visit_type — null/undefined triggers the step-0 fallback (D4 + D6 exception). */
  visitType: VisitType | null | undefined;
  /** Snapshot of the active-meds query at wizard mount (D2). */
  hasActiveMeds: boolean;
  /** Carer-toggled override that surfaces Mobility on a baseline-independent patient. */
  overrideShowMobility?: boolean;
  /** When the active-meds query failed: fail-open (show the step) per spec. */
  medsQueryFailed?: boolean;
}

/**
 * Compute the ordered wizard step list for the current visit context.
 *
 * - 'visit-type' (step-0) is prepended iff `visitType` is null/undefined.
 * - 'vitals' and 'notes' are always present.
 * - 'mobility' iff baselineMobility !== "independent" OR overrideShowMobility.
 *   When baselineMobility is missing/undefined, the step is shown
 *   defensively (fail-open per the spec edge case).
 * - 'clinical' and 'wellbeing' co-fire iff visitType is set AND
 *   visitType !== "standard_wellness". When step-0 is still pending
 *   (visitType nullish), neither is included — they get added by the
 *   wizard's single recomputation after step-0 PATCH succeeds.
 * - 'medication' iff hasActiveMeds OR medsQueryFailed (fail-open).
 * - 'review' is always last (D24).
 */
export function visibleSteps(ctx: ConditionalContext): WizardStep[] {
  const steps: WizardStep[] = [];

  if (ctx.visitType === null || ctx.visitType === undefined) {
    steps.push("visit-type");
  }

  steps.push("vitals");

  if (showMobilityStep(ctx)) {
    steps.push("mobility");
  }

  if (showClinicalAndWellbeingSteps(ctx)) {
    steps.push("clinical", "wellbeing");
  }

  if (showMedicationStep(ctx)) {
    steps.push("medication");
  }

  steps.push("notes");
  steps.push("review");

  return steps;
}

function showMobilityStep(ctx: ConditionalContext): boolean {
  if (ctx.overrideShowMobility) return true;
  // Defensive fail-open when the field is missing — should not happen,
  // baseline_mobility is required at patient enrolment.
  if (ctx.baselineMobility == null) return true;
  return ctx.baselineMobility !== "independent";
}

function showClinicalAndWellbeingSteps(ctx: ConditionalContext): boolean {
  if (ctx.visitType == null) return false;
  return ctx.visitType !== "standard_wellness";
}

function showMedicationStep(ctx: ConditionalContext): boolean {
  if (ctx.medsQueryFailed) return true;
  return ctx.hasActiveMeds;
}

/** Convenience: filter to just section steps (for the submit visible_steps payload). */
export function visibleSectionSteps(ctx: ConditionalContext): SectionStep[] {
  return visibleSteps(ctx).filter((s): s is SectionStep =>
    (SECTION_STEPS as readonly string[]).includes(s),
  );
}
